import React, {useState, useMemo, useCallback, useEffect} from 'react'
import PropTypes from 'prop-types'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {
    Flex,
    ButtonGroup,
    IconButton,
    Skeleton,
    Box,
    Text,
    VStack,
    Fade,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CustomClose} from '../../../components/icons'
import DisplayPrice from '@salesforce/retail-react-app/app/components/display-price'
import Swatch from '../../swatch-group/swatch'
import SwatchGroup from '../../swatch-group'
import ErrorMessage from '../../error-message'
import {useBrand} from '../../../contexts/brand-context'

// Constants
const VARIANT_TYPES = {
    COLOR: 'color',
    SIZE: 'size'
}

const SWATCH_VARIANTS = {
    CIRCLE: 'circle',
    SQUARE: 'square'
}

// Helper functions
const isColorVariant = (id) => id === VARIANT_TYPES.COLOR
const isSizeVariant = (id) => id === VARIANT_TYPES.SIZE
const isDisplayableVariant = (id) => [VARIANT_TYPES.COLOR].includes(id)

/**
 * Component for rendering variant swatches
 */
const VariantSwatch = ({id, value, name, image, href, orderable, isSelected, isFocusable}) => {
    const availabilityTestId = `${id}-${orderable ? 'available' : 'unavailable'}`
    const content = image ? (
        <Box
            height="100%"
            width="100%"
            minWidth="32px"
            backgroundRepeat="no-repeat"
            backgroundSize="cover"
            backgroundColor={name.toLowerCase()}
            backgroundPosition="center center"
            backgroundImage={`url(${image.disBaseLink || image.link})`}
            data-testid={availabilityTestId}
        />
    ) : (
        <Box 
            as="span"
            className="swatch-content"
            whiteSpace="normal"
            overflowWrap="anywhere"
            textAlign="center"
            data-testid={availabilityTestId}
        >
            {name}
        </Box>
    )

    return (
        <Swatch
            isPDP
            key={value}
            href={href}
            disabled={!orderable}
            value={value}
            name={name}
            variant={isColorVariant(id) ? SWATCH_VARIANTS.CIRCLE : SWATCH_VARIANTS.SQUARE}
            selected={isSelected}
            isFocusable={isFocusable}
            id={id}
        >
            {content}
        </Swatch>
    )
}

VariantSwatch.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    image: PropTypes.object,
    href: PropTypes.string,
    orderable: PropTypes.bool,
    isSelected: PropTypes.bool.isRequired,
    isFocusable: PropTypes.bool.isRequired
}

/**
 * Component for rendering a group of variant attributes
 */
const VariantAttributeGroup = ({
    id,
    name,
    selectedValue,
    values,
    intl,
    actualFitComponent,
    sizeChartComponent,
    errorMessage
}) => {
    const {locale} = useMultiSite()
    const isFr = locale?.id?.toLowerCase() === 'fr-ca'

    const swatches = values.map(({href, name, image, value, orderable}, index) => {
        const isSelected = selectedValue?.value === value
        const isFocusable = isSelected || (!selectedValue?.value && index === 0)

        return (
            <VariantSwatch
                key={value}
                id={id}
                value={value}
                name={name}
                image={image}
                href={href}
                orderable={orderable}
                isSelected={isSelected}
                isFocusable={isFocusable}
            />
        )
    })

    const label =
        intl?.formatMessage(
            {
                defaultMessage: '{variantType}',
                id: 'product_view.label.variant_type'
            },
            {variantType: name}
        ) || name

    return (
        <VStack key={id} spacing={2} align="stretch">
            {isSizeVariant(id) && (
                <Flex>
                    {actualFitComponent}
                    {sizeChartComponent}
                </Flex>
            )}
            {errorMessage && (
                <ErrorMessage message={errorMessage} />
            )}
            <SwatchGroup
                isPDP
                value={selectedValue?.value}
                isSizeSwatch={!isColorVariant(id)}
                displayValueOnly={isDisplayableVariant(id)}
                displayName={selectedValue?.name || ''}
                label={errorMessage ? '' : label}
                id={id}
                giftCardValue={id === 'priceValue'}
                giftCardType={id === 'cardType'}
                giftCardTypeSize={isFr ? 2 : 6}
            >
                {swatches}
            </SwatchGroup>
        </VStack>
    )
}

VariantAttributeGroup.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    selectedValue: PropTypes.object,
    values: PropTypes.array.isRequired,
    intl: PropTypes.object,
    actualFitComponent: PropTypes.node,
    sizeChartComponent: PropTypes.node,
    errorMessage: PropTypes.string
}

/**
 * Drawer component for variant selection on mobile
 */
const VariantSelectionDrawer = ({
    isOpen,
    onClose,
    variationAttributes,
    showLoading,
    showError,
    errorMessages,
    intl,
    actualFitComponent,
    sizeChartComponent
}) => {
    const {isGarage} = useBrand()
    if (!isOpen) return null

    const getDrawerTitle = useCallback(() => {
        const nonColorAttributes = variationAttributes.filter(
            (attr) => !isColorVariant(attr.id)
        )
        const firstMissingAttribute = nonColorAttributes
            .filter((attr) => !isColorVariant(attr.id))
            .find((attr) => errorMessages?.[attr.id] || !attr.selectedValue?.value)

        const fallbackAttribute = nonColorAttributes[nonColorAttributes.length - 1]
        const attributeName = (
            firstMissingAttribute?.name ||
            fallbackAttribute?.name ||
            'size'
        ).toLowerCase()

        return (
            (isGarage
                ? intl?.formatMessage(
                      {
                          defaultMessage: 'Select a {attribute}',
                          id: 'product_view.drawer.select_attribute.garage'
                      },
                      {attribute: attributeName}
                  )
                : intl?.formatMessage(
                      {
                          defaultMessage: 'Select a {attribute}',
                          id: 'product_view.drawer.select_attribute.dynamite'
                      },
                      {attribute: attributeName}
                  )) || `Select a ${attributeName}`
        )
    }, [variationAttributes, errorMessages, intl, isGarage])

    const renderVariantContent = () => {
        return variationAttributes
            .filter(({id}) => !isColorVariant(id))
            .map((attribute) => (
                <VariantAttributeGroup
                    key={attribute.id}
                    {...attribute}
                    intl={intl}
                    actualFitComponent={actualFitComponent}
                    sizeChartComponent={sizeChartComponent}
                    errorMessage={showError ? errorMessages?.[attribute.id] : null}
                />
            ))
    }

    return (
        <Fade in={isOpen}>
            <Box
                bg="white"
                borderTop="1px solid"
                borderColor="gray.200"
                boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
                maxH="60vh"
                overflowY="auto"
            >
                <Flex justify="space-between" align="center" px="24px" py="16px" pb="0">
                    <Text fontSize="sm" fontWeight="500">
                        {getDrawerTitle()}
                    </Text>
                    <IconButton
                        aria-label="Close variants"
                        icon={<CustomClose w="24px" h="24px" />}
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                    />
                </Flex>

                <Box px="24px" py="16px">
                    {showLoading ? (
                        <VStack spacing={4}>
                            <Skeleton height={6} width={32} />
                            <Skeleton height={20} width="100%" />
                        </VStack>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {renderVariantContent()}
                        </VStack>
                    )}
                </Box>
            </Box>
        </Fade>
    )
}

VariantSelectionDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    variationAttributes: PropTypes.array.isRequired,
    showLoading: PropTypes.bool,
    showError: PropTypes.bool,
    errorMessages: PropTypes.object,
    intl: PropTypes.object,
    actualFitComponent: PropTypes.node,
    sizeChartComponent: PropTypes.node
}

/**
 * Hook for managing variant validation logic
 */
const useVariantValidation = (variationAttributes, intl) => {
    const {isGarage} = useBrand()
    const [showError, setShowError] = useState(false)

    const hasUnselectedVariants = useMemo(() => {
        return variationAttributes.some(
            (attr) => !attr.selectedValue?.value && !isColorVariant(attr.id)
        )
    }, [variationAttributes])

    const getSelectedVariantsText = useCallback(() => {
        const nonColorAttributes = variationAttributes.filter((attr) => !isColorVariant(attr.id))
        const isValidSelection = (attr) => {
            const val = attr.selectedValue?.value
            if (!val) return false
            if (!attr.values?.length) return !!attr.selectedValue?.name
            return attr.values.some((v) => v.value === val)
        }
        const firstMissingAttribute = nonColorAttributes.find((attr) => !isValidSelection(attr))

        if (firstMissingAttribute) {
            const attributeName = firstMissingAttribute.name.toLowerCase()
            return (
                (isGarage
                    ? intl?.formatMessage(
                          {
                              defaultMessage: 'Select a {attribute}',
                              id: 'product_view.drawer.select_attribute.garage'
                          },
                          {attribute: attributeName}
                      )
                    : intl?.formatMessage(
                          {
                              defaultMessage: 'Select a {attribute}',
                              id: 'product_view.drawer.select_attribute.dynamite'
                          },
                          {attribute: attributeName}
                      )) || `Select a ${attributeName}`
            )
        }

        const selectedValues = nonColorAttributes
            .filter((attr) => isValidSelection(attr))
            .map((attr) => `${attr.selectedValue.name}`)

        if (selectedValues.length === 0) {
            const fallbackName = (nonColorAttributes[0]?.name || 'size').toLowerCase()
            return (
                (isGarage
                    ? intl?.formatMessage(
                          {
                              defaultMessage: 'Select a {attribute}',
                              id: 'product_view.drawer.select_attribute.garage'
                          },
                          {attribute: fallbackName}
                      )
                    : intl?.formatMessage(
                          {
                              defaultMessage: 'Select a {attribute}',
                              id: 'product_view.drawer.select_attribute.dynamite'
                          },
                          {attribute: fallbackName}
                      )) || `Select a ${fallbackName}`
            )
        }

        return selectedValues.join(', ')
    }, [variationAttributes, intl, isGarage])

    const getMissingAttributeMessages = useCallback(() => {
        return variationAttributes
            .filter((attr) => !attr.selectedValue?.value && !isColorVariant(attr.id))
            .reduce((messages, attr) => {
                const message =
                    intl?.formatMessage(
                        {
                            defaultMessage: 'Please select a {attribute}',
                            id: 'product_view.error.missing_single_attribute'
                        },
                        {attribute: attr.name.toLowerCase()}
                    ) || `Please select a ${attr.name.toLowerCase()}`
                messages[attr.id] = message
                return messages
            }, {})
    }, [variationAttributes, intl])

    // Clear error when selections are complete
    useEffect(() => {
        if (!hasUnselectedVariants && showError) {
            setShowError(false)
        }
    }, [hasUnselectedVariants, showError])

    return {
        hasUnselectedVariants,
        showError,
        setShowError,
        getSelectedVariantsText,
        getMissingAttributeMessages
    }
}

/**
 * Hook for handling mobile action button behavior
 */
const useMobileActionButtons = (
    renderActionButtons,
    hasUnselectedVariants,
    showVariants,
    setShowVariants,
    setShowError,
    hasGiftCardFormErrors,
    scrollToGiftCardForm
) => {
    return useCallback(() => {
        const originalButtons = renderActionButtons?.({scrollErrorIntoView: false})
        if (!originalButtons) return null

        // Helper function to determine if button is main action
        const isMainActionButton = (button) => {
            return (
                React.isValidElement(button) &&
                button?.props?.onClick &&
                !button?.props?.['aria-label']?.includes('wishlist')
            )
        }

        // Helper function to create modified click handler
        const createModifiedClickHandler = (originalOnClick) => {
            return async (...args) => {
                if (hasUnselectedVariants && !showVariants) {
                    setShowVariants(true)
                } else if (hasUnselectedVariants && showVariants) {
                    setShowError(true)
                } else {
                    await originalOnClick?.(...args)
                    setShowVariants(false)
                    if (hasGiftCardFormErrors?.()) {
                        scrollToGiftCardForm?.()
                    }
                }
            }
        }

        return React.Children.map(originalButtons, (child) => {
            // Handle wrapped action components (e.g. ActionButtons) exposing onCartItem
            if (React.isValidElement(child) && child?.props?.onCartItem) {
                return React.cloneElement(child, {
                    onCartItem: createModifiedClickHandler(child?.props?.onCartItem)
                })
            }

            // Handle ButtonGroup
            if (React.isValidElement(child) && child.type === ButtonGroup) {
                return React.cloneElement(child, {
                    children: React.Children.map(child?.props?.children, (button) => {
                        if (isMainActionButton(button)) {
                            return React.cloneElement(button, {
                                onClick: createModifiedClickHandler(button?.props?.onClick)
                            })
                        }
                        return button
                    })
                })
            }

            // Handle standalone buttons
            if (React.isValidElement(child) && child?.props?.onClick) {
                return React.cloneElement(child, {
                    onClick: createModifiedClickHandler(child?.props?.onClick)
                })
            }

            return child
        })
    }, [
        renderActionButtons,
        hasUnselectedVariants,
        showVariants,
        setShowVariants,
        setShowError,
        hasGiftCardFormErrors,
        scrollToGiftCardForm
    ])
}

/**
 * Hook for handling desktop action button behavior
 */
const useDesktopActionButtons = (
    renderActionButtons,
    hasUnselectedVariants,
    scrollToProductDetails,
    hasGiftCardFormErrors,
    scrollToGiftCardForm
) => {
    return useCallback(() => {
        const originalButtons = renderActionButtons?.({scrollErrorIntoView: false})
        if (!originalButtons) return null

        const isMainActionButton = (button) => {
            return (
                React.isValidElement(button) &&
                button?.props?.onClick &&
                !button?.props?.['aria-label']?.includes('wishlist')
            )
        }

        const createModifiedClickHandler = (originalOnClick) => {
            return async (...args) => {
                if (hasUnselectedVariants) {
                    scrollToProductDetails?.()
                }
                await originalOnClick?.(...args)
                if (hasGiftCardFormErrors?.()) {
                    scrollToGiftCardForm?.()
                }
            }
        }

        return React.Children.map(originalButtons, (child) => {
            // Handle wrapped action components (e.g. ActionButtons) exposing onCartItem
            if (React.isValidElement(child) && child?.props?.onCartItem) {
                return React.cloneElement(child, {
                    onCartItem: createModifiedClickHandler(child?.props?.onCartItem)
                })
            }

            if (React.isValidElement(child) && child.type === ButtonGroup) {
                return React.cloneElement(child, {
                    children: React.Children.map(child?.props?.children, (button) => {
                        if (isMainActionButton(button)) {
                            return React.cloneElement(button, {
                                onClick: createModifiedClickHandler(button?.props?.onClick)
                            })
                        }
                        return button
                    })
                })
            }

            if (React.isValidElement(child) && child?.props?.onClick) {
                return React.cloneElement(child, {
                    onClick: createModifiedClickHandler(child?.props?.onClick)
                })
            }

            return child
        })
    }, [
        renderActionButtons,
        hasUnselectedVariants,
        scrollToProductDetails,
        hasGiftCardFormErrors,
        scrollToGiftCardForm
    ])
}

/**
 * Mobile layout component
 */
const MobileLayout = ({
    renderMobileActionButtons
}) => (
    <Box w="100%" display="flex" justifyContent="center">
        {renderMobileActionButtons()}
    </Box>
)

MobileLayout.propTypes = {
    renderMobileActionButtons: PropTypes.func.isRequired
}

/**
 * Desktop layout component
 */
const DesktopLayout = ({
    name,
    priceData,
    isProductPartOfBundle,
    currency,
    getSelectedVariantsText,
    handleVariantClick,
    renderActionButtons
}) => (
    <>
        <Box flex={2}>
            <Skeleton isLoaded={!!name}>
                <Text as="h2" textStyle="labelXs">
                    {name}
                </Text>
            </Skeleton>
            {!isProductPartOfBundle && priceData?.currentPrice && (
                <DisplayPrice
                    currentPriceProps={{
                        textStyle: 'labelXs',
                        ...(priceData?.isOnSale && {color: 'webTypo.textSale'}),
                        mr: '12px'
                    }}
                    listPriceProps={{
                        textStyle: 'labelXs',
                        color: 'webTypo.textStrikethrough',
                        textDecoration: priceData?.isOnSale ? 'line-through' : 'none'
                    }}
                    priceData={priceData}
                    currency={currency}
                />
            )}
            <Box>
                <Text
                    onClick={handleVariantClick}
                    onKeyDown={(e) => e.key === 'Enter' && handleVariantClick?.(e)}
                    role="button"
                    tabIndex={0}
                    aria-label={getSelectedVariantsText()}
                    cursor="pointer"
                    fontSize="xs"
                    textDecoration="underline"
                    color="webTypo.textPrimary"
                    w="max-content"
                    data-testid="pdp-button-selected-variants"
                >
                   {getSelectedVariantsText()}
                </Text>
            </Box>
        </Box>
        <Box flexShrink={0} sx={{'[data-testid^="add-to-bag"]': {flex: '0 0 385px'}}}>
            {renderActionButtons?.()}
        </Box>
    </>
)

DesktopLayout.propTypes = {
    name: PropTypes.string,
    priceData: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    currency: PropTypes.string,
    getSelectedVariantsText: PropTypes.func.isRequired,
    handleVariantClick: PropTypes.func.isRequired,
    renderActionButtons: PropTypes.func
}

/**
 * Main sticky button component for product actions
 */
const StickyButton = ({
    name,
    currency,
    priceData,
    isProductPartOfBundle,
    renderActionButtons,
    productDetailsRef,
    giftCardFormContainerRef,
    hasGiftCardFormErrors,
    variationAttributes = [],
    showLoading = false,
    intl,
    actualFitComponent,
    sizeChartComponent
}) => {
    const [showVariants, setShowVariants] = useState(false)
    const [hasMounted, setHasMounted] = useState(false)
    const isMobile = useBreakpointValue({base: true, lg: false})

    useEffect(() => {
        setHasMounted(true)
    }, [])

    const {
        hasUnselectedVariants,
        showError,
        setShowError,
        getSelectedVariantsText,
        getMissingAttributeMessages
    } = useVariantValidation(variationAttributes, intl)

    const scrollToProductDetails = useCallback(() => {
        if (productDetailsRef?.current) {
            productDetailsRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        }
    }, [productDetailsRef])

    const scrollToGiftCardForm = useCallback(() => {
        if (giftCardFormContainerRef?.current) {
            giftCardFormContainerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        }
    }, [giftCardFormContainerRef])

    const handleVariantClick = useCallback(() => {
        if (!isMobile) {
            scrollToProductDetails()
        }
    }, [isMobile, scrollToProductDetails])

    const handleCloseVariants = useCallback(() => {
        setShowVariants(false)
        setShowError(false)
    }, [setShowError])

    const renderMobileActionButtons = useMobileActionButtons(
        renderActionButtons,
        hasUnselectedVariants,
        showVariants,
        setShowVariants,
        setShowError,
        hasGiftCardFormErrors,
        scrollToGiftCardForm
    )
    const renderDesktopActionButtons = useDesktopActionButtons(
        renderActionButtons,
        hasUnselectedVariants,
        scrollToProductDetails,
        hasGiftCardFormErrors,
        scrollToGiftCardForm
    )

    if (!hasMounted) {
        return null
    }

    return (
        <Box position="fixed" bottom="0" left="0" w="100%" zIndex={1002} className="sticky-button">
            {/* Variants Drawer for Mobile */}
            {isMobile && (
                <VariantSelectionDrawer
                    isOpen={showVariants}
                    onClose={handleCloseVariants}
                    variationAttributes={variationAttributes}
                    showLoading={showLoading}
                    showError={showError && hasUnselectedVariants}
                    errorMessages={getMissingAttributeMessages()}
                    intl={intl}
                    actualFitComponent={actualFitComponent}
                    sizeChartComponent={sizeChartComponent}
                />
            )}

            {/* Sticky Button Bar */}
            <Box
                bg="white"
                w="100%"
                py="16px"
                borderTop={showVariants ? '0' : '1px solid'}
                borderColor="gray.200"
            >
                <Flex
                    px="24px"
                    justifyContent="space-between"
                    alignItems={isMobile ? 'start' : 'center'}
                    width="100%"
                    maxWidth="container.xxxl"
                    marginLeft="auto"
                    marginRight="auto"
                    gap={4}
                    flexDirection={isMobile ? 'column' : 'row'}
                >
                    {isMobile ? (
                        <MobileLayout
                            hasUnselectedVariants={hasUnselectedVariants}
                            getSelectedVariantsText={getSelectedVariantsText}
                            scrollToProductDetails={scrollToProductDetails}
                            renderMobileActionButtons={renderMobileActionButtons}
                        />
                    ) : (
                        <DesktopLayout
                            name={name}
                            priceData={priceData}
                            isProductPartOfBundle={isProductPartOfBundle}
                            currency={currency}
                            getSelectedVariantsText={getSelectedVariantsText}
                            handleVariantClick={handleVariantClick}
                            renderActionButtons={renderDesktopActionButtons}
                        />
                    )}
                </Flex>
            </Box>
        </Box>
    )
}

StickyButton.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    priceData: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    renderActionButtons: PropTypes.func,
    productDetailsRef: PropTypes.object,
    giftCardFormContainerRef: PropTypes.object,
    hasGiftCardFormErrors: PropTypes.func,
    variationAttributes: PropTypes.array,
    showLoading: PropTypes.bool,
    intl: PropTypes.object,
    actualFitComponent: PropTypes.node,
    sizeChartComponent: PropTypes.node
}

export default StickyButton

